import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../data/translations';
const Header = ({ companyName, logo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, toggleLanguage, localizedPath } = useLanguage();
  const t = translations[language];
  const navigate = useNavigate();
  const location = useLocation();

  const isHome = location.pathname === '/' || location.pathname === '/pt';
  // Transparent only on home page when at the top.
  const transparent = isHome && !scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 80);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll when mobile drawer is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen]);

  const navItems = [
    { key: 'advisory', route: localizedPath('/advisory'), label: t.advisory },
    { key: 'services', route: localizedPath('/services'), label: t.services },
    { key: 'regions', route: localizedPath('/regions'), label: t.regions },
    { key: 'international', route: localizedPath('/international'), label: t.international },
    { key: 'journal', route: localizedPath('/journal'), label: t.journal },
    { key: 'contact', route: localizedPath('/contact'), label: t.contact },
  ];

  const handleNavClick = (item) => {
    setIsMenuOpen(false);
    navigate(item.route);
  };

  const linkBase = 'transition-colors duration-300';
  const linkStyle = {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: '0.68rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    fontWeight: 500,
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          transparent
            ? 'bg-transparent border-b border-transparent'
            : 'bg-editorial-bone/95 backdrop-blur-md border-b border-editorial-sand-deep/40'
        }`}
        data-testid="luxury-header"
      >
        <div className={`px-6 md:px-10 lg:px-16 xl:px-20 transition-all duration-500 ${transparent ? 'py-6' : 'py-4'}`}>
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link
              to={localizedPath('/')}
              className="flex items-center hover:opacity-80 transition-opacity"
              data-testid="header-logo"
              aria-label={companyName || 'Your Broker Portugal'}
            >
              {logo ? (
                <img
                  src={logo}
                  alt={companyName || 'Your Broker Portugal'}
                  className={`w-auto object-contain transition-all duration-500 ${transparent ? 'h-11' : 'h-10'}`}
                  style={{ filter: transparent ? 'brightness(0) invert(1)' : 'none' }}
                />
              ) : (
                <span
                  className={`tracking-[0.22em] uppercase ${transparent ? 'text-editorial-bone' : 'text-editorial-charcoal'}`}
                  style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.78rem', fontWeight: 500 }}
                >
                  {companyName || 'Your Broker · Portugal'}
                </span>
              )}
            </Link>

            {/* Desktop nav */}
            <nav className="hidden lg:flex items-center gap-10">
              {navItems.map((item) => (
                <button
                  key={item.key}
                  onClick={() => handleNavClick(item)}
                  className={`${linkBase} ${
                    transparent
                      ? 'text-editorial-bone/80 hover:text-editorial-bone'
                      : 'text-editorial-charcoal-soft hover:text-editorial-bronze'
                  }`}
                  style={linkStyle}
                  data-testid={`nav-${item.key}`}
                >
                  {item.label}
                </button>
              ))}

              {/* Language toggle */}
              <button
                onClick={toggleLanguage}
                aria-label="Toggle language"
                data-testid="lang-toggle"
                className={`flex items-center gap-1.5 ${linkBase} ${
                  transparent
                    ? 'text-editorial-bone/60 hover:text-editorial-bone'
                    : 'text-editorial-muted hover:text-editorial-charcoal'
                }`}
                style={linkStyle}
              >
                <span className={language === 'pt' ? 'opacity-100' : 'opacity-45'}>PT</span>
                <span className="opacity-30">·</span>
                <span className={language === 'en' ? 'opacity-100' : 'opacity-45'}>EN</span>
              </button>
            </nav>

            {/* Mobile burger */}
            <div className="lg:hidden flex items-center gap-5">
              <button
                onClick={toggleLanguage}
                aria-label="Toggle language"
                data-testid="lang-toggle-mobile"
                className={linkBase}
                style={{ ...linkStyle, fontSize: '0.62rem' }}
              >
                <span className={`${transparent ? 'text-editorial-bone' : 'text-editorial-charcoal'}`}>
                  {language === 'pt' ? 'EN' : 'PT'}
                </span>
              </button>
              <button
                onClick={() => setIsMenuOpen(true)}
                aria-label="Open menu"
                data-testid="menu-open-button"
                className={`p-1 ${transparent ? 'text-editorial-bone' : 'text-editorial-charcoal'}`}
              >
                <Menu className="h-6 w-6" strokeWidth={1.4} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile drawer */}
      <div
        className={`fixed inset-0 z-[60] lg:hidden transition-all duration-500 ${
          isMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
        data-testid="mobile-drawer"
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-editorial-charcoal/95 backdrop-blur-sm"
          onClick={() => setIsMenuOpen(false)}
        />
        {/* Panel */}
        <div
          className={`absolute inset-0 flex flex-col bg-editorial-charcoal text-editorial-bone transition-transform duration-500 ${
            isMenuOpen ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-6 py-6 border-b border-editorial-bone/10">
            <span
              className="text-editorial-bronze-lt"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.62rem', letterSpacing: '0.22em', textTransform: 'uppercase' }}
            >
              {t.menu}
            </span>
            <button
              onClick={() => setIsMenuOpen(false)}
              aria-label="Close menu"
              data-testid="menu-close-button"
              className="text-editorial-bone p-1"
            >
              <X className="h-6 w-6" strokeWidth={1.4} />
            </button>
          </div>

          <nav className="flex-1 px-6 py-10 flex flex-col">
            {navItems.map((item, i) => (
              <button
                key={item.key}
                onClick={() => handleNavClick(item)}
                className="text-left py-5 border-b border-editorial-bone/8 text-editorial-bone hover:text-editorial-bronze-lt transition-colors"
                style={{
                  fontFamily: 'Cormorant Garamond, serif',
                  fontSize: '1.85rem',
                  fontWeight: 300,
                  lineHeight: 1.2,
                  letterSpacing: '0.01em',
                }}
                data-testid={`mobile-nav-${item.key}`}
              >
                <span className="text-editorial-bronze-lt/60 mr-3" style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.6rem', letterSpacing: '0.2em' }}>
                  0{i + 1}
                </span>
                {item.label}
              </button>
            ))}
          </nav>

          <div className="px-6 py-8 border-t border-editorial-bone/10">
            <p
              className="text-editorial-bronze-lt mb-2"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.58rem', letterSpacing: '0.22em', textTransform: 'uppercase' }}
            >
              {t.privateAdvisory}
            </p>
            <a
              href="mailto:portugal@yourbrokerportugal.com"
              className="block text-editorial-bone/65 hover:text-editorial-bronze-lt"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', fontWeight: 300 }}
            >
              portugal@yourbrokerportugal.com
            </a>
            <a
              href="tel:+351931997415"
              className="block text-editorial-bone/65 hover:text-editorial-bronze-lt mt-1.5"
              style={{ fontFamily: 'DM Sans, sans-serif', fontSize: '0.85rem', fontWeight: 300 }}
            >
              +351 931 997 415
            </a>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
